/**
 * SVG pattern definitions for crate covers. Each pattern is a small
 * repeating SVG that we render as a full-bleed <svg> with a user-chosen
 * tint color. Patterns are intentionally minimal (single color +
 * transparent background) so the tint shows through.
 *
 * How rendering works:
 *   • The <PatternCover> component renders a fixed background color
 *     (`bg`) with the pattern <svg> layered on top.
 *   • The pattern strokes/fills use `currentColor` so the parent's
 *     `color: <tint>` prop applies uniformly.
 *
 * Adding a pattern: append an object { id, label, bg, svg } where `svg`
 * is a JSX snippet (no closing <svg> wrapper; that's added at render time).
 */

import React from 'react';

export const PATTERNS = [
  {
    id: 'dots',
    label: 'Dots',
    bg: '#0F1116',
    svg: (
      <pattern id="p-dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
        <circle cx="12" cy="12" r="2.5" fill="currentColor" opacity="0.75" />
      </pattern>
    ),
  },
  {
    id: 'grid',
    label: 'Grid',
    bg: '#0F1116',
    svg: (
      <pattern id="p-grid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
        <path d="M32 0 L0 0 0 32" fill="none" stroke="currentColor" strokeOpacity="0.7" strokeWidth="1" />
      </pattern>
    ),
  },
  {
    id: 'waves',
    label: 'Waves',
    bg: '#0F1116',
    svg: (
      <pattern id="p-waves" x="0" y="0" width="56" height="20" patternUnits="userSpaceOnUse">
        <path
          d="M0 10 Q 14 0, 28 10 T 56 10"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.85"
          strokeWidth="2"
        />
      </pattern>
    ),
  },
  {
    id: 'chevron',
    label: 'Chevron',
    bg: '#0F1116',
    svg: (
      <pattern id="p-chevron" x="0" y="0" width="32" height="24" patternUnits="userSpaceOnUse">
        <path
          d="M0 20 L16 4 L32 20"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.75"
          strokeWidth="2"
        />
      </pattern>
    ),
  },
  {
    id: 'diagonal',
    label: 'Diagonal',
    bg: '#0F1116',
    svg: (
      <pattern id="p-diagonal" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="24" stroke="currentColor" strokeOpacity="0.8" strokeWidth="3" />
      </pattern>
    ),
  },
  {
    id: 'triangles',
    label: 'Triangles',
    bg: '#0F1116',
    svg: (
      <pattern id="p-triangles" x="0" y="0" width="32" height="28" patternUnits="userSpaceOnUse">
        <polygon points="16,2 30,26 2,26" fill="currentColor" fillOpacity="0.75" />
      </pattern>
    ),
  },
  {
    id: 'discs',
    label: 'Discs',
    bg: '#0F1116',
    svg: (
      <pattern id="p-discs" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
        <circle cx="20" cy="20" r="12" fill="none" stroke="currentColor" strokeOpacity="0.85" strokeWidth="2" />
        <circle cx="20" cy="20" r="3" fill="currentColor" opacity="0.85" />
      </pattern>
    ),
  },
  {
    id: 'crosshatch',
    label: 'Crosshatch',
    bg: '#0F1116',
    svg: (
      <pattern id="p-crosshatch" x="0" y="0" width="16" height="16" patternUnits="userSpaceOnUse">
        <path d="M0 0 L16 16 M16 0 L0 16" stroke="currentColor" strokeOpacity="0.65" strokeWidth="1" />
      </pattern>
    ),
  },
  {
    id: 'plus',
    label: 'Plus',
    bg: '#0F1116',
    svg: (
      <pattern id="p-plus" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
        <path d="M14 8 L14 20 M8 14 L20 14" stroke="currentColor" strokeOpacity="0.85" strokeWidth="2" />
      </pattern>
    ),
  },
  {
    id: 'stars',
    label: 'Stars',
    bg: '#0F1116',
    svg: (
      <pattern id="p-stars" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
        <path
          d="M24 12 L26 20 L34 22 L26 24 L24 32 L22 24 L14 22 L22 20 Z"
          fill="currentColor"
          fillOpacity="0.85"
        />
      </pattern>
    ),
  },
  {
    id: 'vinyl',
    label: 'Vinyl',
    bg: '#0F1116',
    svg: (
      <pattern id="p-vinyl" x="0" y="0" width="56" height="56" patternUnits="userSpaceOnUse">
        <circle cx="28" cy="28" r="22" fill="none" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
        <circle cx="28" cy="28" r="16" fill="none" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
        <circle cx="28" cy="28" r="10" fill="none" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1" />
        <circle cx="28" cy="28" r="3" fill="currentColor" opacity="0.85" />
      </pattern>
    ),
  },
  {
    id: 'sound-bars',
    label: 'Sound Bars',
    bg: '#0F1116',
    svg: (
      <pattern id="p-sound-bars" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
        <rect x="4" y="14" width="3" height="14" fill="currentColor" fillOpacity="0.8" />
        <rect x="11" y="6" width="3" height="22" fill="currentColor" fillOpacity="0.8" />
        <rect x="18" y="10" width="3" height="18" fill="currentColor" fillOpacity="0.8" />
        <rect x="25" y="18" width="3" height="10" fill="currentColor" fillOpacity="0.8" />
      </pattern>
    ),
  },
];

export function getPatternById(id) {
  return PATTERNS.find((p) => p.id === id) || PATTERNS[0];
}
